/**
 * 用途：渠道监控页，按渠道 / 时间维度汇总广告数据，用于跨渠道效果对比分析
 * 路由：/ad-analysis/channel-monitoring
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { channelMonitoringApi, type ChannelRow, type AllViewsResponse } from '../api/channelMonitoring';
import useAppStore from '../store/useAppStore';
import { MultiSelect, type SelectOption } from '../components/MultiSelect';
import { TableLoadingCell, TableLoadingOverlay } from '../components/TableLoadingOverlay';
import { sf, type FieldKey } from '../lib/fieldPolicy';
import { SortableHeader, sortRows, useTableSort, type SortValue } from '../components/SortableTable';

// ── Date helpers ───────────────────────────────────────────────────────────

function daysAgo(n: number): string {
  const d = new Date(); d.setDate(d.getDate() - (n - 1));
  return d.toISOString().slice(0, 10);
}
function today(): string { return new Date().toISOString().slice(0, 10); }

type PeriodType = 'day' | 'week' | 'month' | 'quarter' | 'year';

// ── Period option generators ──────────────────────────────────────────────

function getISOWeek(d: Date): number {
  const tmp = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  tmp.setUTCDate(tmp.getUTCDate() + 4 - (tmp.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(tmp.getUTCFullYear(), 0, 1));
  return Math.ceil(((tmp.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
}

function mondayOfWeek(year: number, week: number): string {
  const jan4 = new Date(Date.UTC(year, 0, 4));
  const dayOfWeek = jan4.getUTCDay() || 7;
  const firstMonday = new Date(jan4);
  firstMonday.setUTCDate(jan4.getUTCDate() - dayOfWeek + 1);
  const target = new Date(firstMonday);
  target.setUTCDate(firstMonday.getUTCDate() + (week - 1) * 7);
  return target.toISOString().slice(0, 10);
}

interface PeriodOption { label: string; value: string; }

function fmtShortDate(dateStr: string): string {
  return dateStr.slice(5).replace('-', '.');
}

function generateWeekOptions(): PeriodOption[] {
  const opts: PeriodOption[] = [];
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentWeek = getISOWeek(now);
  for (let y = currentYear; y >= currentYear - 1; y--) {
    const maxWeek = y === currentYear ? currentWeek : 52;
    for (let w = maxWeek; w >= 1; w--) {
      const monday = mondayOfWeek(y, w);
      const sun = new Date(monday);
      sun.setDate(sun.getDate() + 6);
      const sunday = sun.toISOString().slice(0, 10);
      opts.push({ label: `${y}年第${w}周 (${fmtShortDate(monday)}~${fmtShortDate(sunday)})`, value: monday });
    }
  }
  return opts;
}

function generateMonthOptions(): PeriodOption[] {
  const opts: PeriodOption[] = [];
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;
  for (let y = currentYear; y >= currentYear - 1; y--) {
    const maxM = y === currentYear ? currentMonth : 12;
    for (let m = maxM; m >= 1; m--) {
      const mm = String(m).padStart(2, '0');
      opts.push({ label: `${y}年${m}月`, value: `${y}-${mm}-01` });
    }
  }
  return opts;
}

function generateQuarterOptions(): PeriodOption[] {
  const opts: PeriodOption[] = [];
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentQ = Math.ceil((now.getMonth() + 1) / 3);
  for (let y = currentYear; y >= currentYear - 1; y--) {
    const maxQ = y === currentYear ? currentQ : 4;
    for (let q = maxQ; q >= 1; q--) {
      const mm = String((q - 1) * 3 + 1).padStart(2, '0');
      opts.push({ label: `${y}年第${q}季度`, value: `${y}-${mm}-01` });
    }
  }
  return opts;
}

function generateYearOptions(): PeriodOption[] {
  const opts: PeriodOption[] = [];
  const currentYear = new Date().getFullYear();
  for (let y = currentYear; y >= currentYear - 2; y--) {
    opts.push({ label: `${y}年`, value: `${y}-01-01` });
  }
  return opts;
}

// ── Formatters ─────────────────────────────────────────────────────────────

const fmtNum = (v: number) => v == null ? '-' : v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtInt = (v: number) => v == null ? '-' : v.toLocaleString('en-US');
const fmtPct = (v: number) => v == null ? '-' : (v * 100).toFixed(2) + '%';

function supportsReach(row: ChannelRow): boolean {
  return row.reach_supported !== false;
}

// ── Sum helper ─────────────────────────────────────────────────────────────

function sumChannelRows(rows: ChannelRow[], label: string): ChannelRow {
  const s = (fn: (r: ChannelRow) => number) => rows.reduce((a, r) => a + fn(r), 0);
  const supportedReachRows = rows.filter(supportsReach);
  const sr = (fn: (r: ChannelRow) => number) => supportedReachRows.reduce((a, r) => a + fn(r), 0);
  const totalSpend = s(r => r.spend);
  const totalImpressions = s(r => r.impressions);
  const totalClicks = s(r => r.clicks);
  const totalReach = sr(r => r.reach);
  const totalReachImpressions = sr(r => r.impressions);
  const totalAddToCart = s(r => r.add_to_cart);
  const totalPurchases = s(r => r.purchases);
  const totalRevenue = s(r => r.revenue);
  const totalPaidUserNum = s(r => r.paid_user_num);
  const totalNewBasePayable = s(r => r.new_base_payable);
  const totalNewOrderNum = s(r => r.new_order_num);
  const totalNewUserNum = s(r => r.new_user_num);
  const totalFirstOrderPayable = s(r => r.first_order_payable);
  const totalFirstOrderOrderNum = s(r => r.first_order_order_num);
  return {
    channel: label,
    spend: totalSpend,
    impressions: totalImpressions,
    reach: totalReach,
    reach_supported: supportedReachRows.length > 0,
    frequency: totalReach > 0 ? totalReachImpressions / totalReach : 0,
    clicks: totalClicks,
    add_to_cart: totalAddToCart,
    purchases: totalPurchases,
    revenue: totalRevenue,
    registrations: s(r => r.registrations),
    cpm: totalImpressions > 0 ? (totalSpend / totalImpressions) * 1000 : 0,
    ctr: totalImpressions > 0 ? totalClicks / totalImpressions : 0,
    cpc: totalClicks > 0 ? totalSpend / totalClicks : 0,
    add_to_cart_cost: totalAddToCart > 0 ? totalSpend / totalAddToCart : 0,
    cpa: totalPurchases > 0 ? totalSpend / totalPurchases : 0,
    roi: totalSpend > 0 ? totalRevenue / totalSpend : 0,
    aov: totalPurchases > 0 ? totalRevenue / totalPurchases : 0,
    add_to_cart_rate: totalClicks > 0 ? totalAddToCart / totalClicks : 0,
    cart_conversion_rate: totalAddToCart > 0 ? totalPurchases / totalAddToCart : 0,
    purchase_rate: totalClicks > 0 ? totalPurchases / totalClicks : 0,
    paid_user_num: totalPaidUserNum,
    order_num: s(r => r.order_num),
    new_base_payable: totalNewBasePayable,
    new_order_num: totalNewOrderNum,
    new_user_num: totalNewUserNum,
    new_user_rate: totalPaidUserNum > 0 ? totalNewUserNum / totalPaidUserNum : 0,
    new_aov: totalNewOrderNum > 0 ? totalNewBasePayable / totalNewOrderNum : 0,
    new_arpu: totalNewUserNum > 0 ? totalNewBasePayable / totalNewUserNum : 0,
    first_order_payable: totalFirstOrderPayable,
    first_order_order_num: totalFirstOrderOrderNum,
    first_order_aov: totalFirstOrderOrderNum > 0 ? totalFirstOrderPayable / totalFirstOrderOrderNum : 0,
  };
}

// ── Table component ────────────────────────────────────────────────────────

interface TableDef {
  key: keyof ChannelRow;
  label: string;
  fmt: (v: number) => string;
  permKey?: FieldKey;
}

const BASE_COLS: TableDef[] = [
  { key: 'channel', label: '渠道', fmt: (v) => String(v) },
  { key: 'spend', label: '花费', fmt: fmtNum, permKey: 'spend' },
  { key: 'impressions', label: '展示', fmt: fmtInt },
  { key: 'reach', label: '覆盖人数', fmt: fmtInt },
  { key: 'frequency', label: '频次', fmt: fmtNum },
  { key: 'clicks', label: '点击量', fmt: fmtInt },
];

const ADD_TO_CART_COL: TableDef = { key: 'add_to_cart', label: '加购数', fmt: fmtNum };
const PURCHASES_COL: TableDef = { key: 'purchases', label: '购物', fmt: fmtNum };
const REVENUE_COL: TableDef = { key: 'revenue', label: '收入', fmt: fmtNum, permKey: 'revenue' };
const REGISTRATIONS_COL: TableDef = { key: 'registrations', label: '注册', fmt: fmtNum };
const CPM_COL: TableDef = { key: 'cpm', label: 'CPM', fmt: fmtNum, permKey: 'cpm' };
const CTR_COL: TableDef = { key: 'ctr', label: 'CTR', fmt: fmtPct };
const CPC_COL: TableDef = { key: 'cpc', label: 'CPC', fmt: fmtNum, permKey: 'cpc' };
const ADD_TO_CART_COST_COL: TableDef = { key: 'add_to_cart_cost', label: '加购成本', fmt: fmtNum, permKey: 'cpa' };
const CPA_COL: TableDef = { key: 'cpa', label: 'CPA', fmt: fmtNum, permKey: 'cpa' };
const ROI_COL: TableDef = { key: 'roi', label: 'ROI', fmt: fmtNum, permKey: 'roas' };
const AOV_COL: TableDef = { key: 'aov', label: '客单价', fmt: fmtNum, permKey: 'revenue' };
const ADD_TO_CART_RATE_COL: TableDef = { key: 'add_to_cart_rate', label: '加购率', fmt: fmtPct };
const CART_CONV_RATE_COL: TableDef = { key: 'cart_conversion_rate', label: '购物车转化率', fmt: fmtPct };
const PURCHASE_RATE_COL: TableDef = { key: 'purchase_rate', label: '购买率', fmt: fmtPct };

const PAID_USER_NUM_COL: TableDef = { key: 'paid_user_num', label: '付费用户数', fmt: fmtInt };
const ORDER_NUM_COL: TableDef = { key: 'order_num', label: '订单数', fmt: fmtInt };
const NEW_BASE_PAYABLE_COL: TableDef = { key: 'new_base_payable', label: '新客金额', fmt: fmtNum, permKey: 'revenue' };
const NEW_ORDER_NUM_COL: TableDef = { key: 'new_order_num', label: '新客订单数', fmt: fmtInt };
const NEW_USER_NUM_COL: TableDef = { key: 'new_user_num', label: '新客人数', fmt: fmtInt };
const NEW_USER_RATE_COL: TableDef = { key: 'new_user_rate', label: '新客占比', fmt: fmtPct };
const NEW_AOV_COL: TableDef = { key: 'new_aov', label: '新客客单价', fmt: fmtNum, permKey: 'revenue' };
const NEW_ARPU_COL: TableDef = { key: 'new_arpu', label: '新客人均消费', fmt: fmtNum, permKey: 'revenue' };
const FIRST_ORDER_PAYABLE_COL: TableDef = { key: 'first_order_payable', label: '首单金额', fmt: fmtNum, permKey: 'revenue' };
const FIRST_ORDER_AOV_COL: TableDef = { key: 'first_order_aov', label: '首单客单价', fmt: fmtNum, permKey: 'revenue' };

const RATIO_COLS: TableDef[] = [
  CPM_COL, CTR_COL, CPC_COL, ADD_TO_CART_COST_COL, CPA_COL, ROI_COL, AOV_COL,
  ADD_TO_CART_RATE_COL, CART_CONV_RATE_COL, PURCHASE_RATE_COL,
];

const NEW_CUSTOMER_COLS: TableDef[] = [
  PAID_USER_NUM_COL, ORDER_NUM_COL, NEW_BASE_PAYABLE_COL, NEW_ORDER_NUM_COL, NEW_USER_NUM_COL,
  NEW_USER_RATE_COL, NEW_AOV_COL, NEW_ARPU_COL, FIRST_ORDER_PAYABLE_COL, FIRST_ORDER_AOV_COL,
];

const COLS_SUMMARY   = [...BASE_COLS, ...RATIO_COLS, ...NEW_CUSTOMER_COLS];
const COLS_UPPER     = [...BASE_COLS, ADD_TO_CART_COL, ...RATIO_COLS];
const COLS_MIDDLE    = [...BASE_COLS, ADD_TO_CART_COL, ...RATIO_COLS];
const COLS_LOWER     = [...BASE_COLS, ADD_TO_CART_COL, ...RATIO_COLS];
const COLS_LAYER_TOT = [...BASE_COLS, ADD_TO_CART_COL, PURCHASES_COL, REVENUE_COL, ...RATIO_COLS];
const COLS_DETAIL    = [...BASE_COLS, ADD_TO_CART_COL, PURCHASES_COL, REGISTRATIONS_COL, ...RATIO_COLS];

function formatPeriodLabel(period: string, periodType: PeriodType): string {
  if (!period) return '';
  const d = period.slice(0, 10);
  switch (periodType) {
    case 'day':
      return d.slice(5).replace('-', '.');
    case 'week': {
      const dt = new Date(d);
      const y = dt.getFullYear();
      const w = getISOWeek(dt);
      const sun = new Date(dt);
      sun.setDate(sun.getDate() + 6);
      return `${y}年第${w}周 (${fmtShortDate(d)}~${fmtShortDate(sun.toISOString().slice(0, 10))})`;
    }
    case 'month': {
      const [y, m] = d.split('-');
      return `${y}年${parseInt(m)}月`;
    }
    case 'quarter': {
      const [y, m] = d.split('-');
      const q = Math.ceil(parseInt(m) / 3);
      return `${y}年Q${q}`;
    }
    case 'year':
      return `${d.slice(0, 4)}年`;
    default:
      return d;
  }
}

interface PeriodGroup {
  period: string;
  label: string;
  rows: ChannelRow[];
}

function groupByPeriod(rows: ChannelRow[], periodType: PeriodType): PeriodGroup[] {
  const groups: PeriodGroup[] = [];
  const map = new Map<string, ChannelRow[]>();
  const order: string[] = [];
  for (const row of rows) {
    const key = row.period || '';
    if (!map.has(key)) {
      map.set(key, []);
      order.push(key);
    }
    map.get(key)!.push(row);
  }
  for (const key of order) {
    groups.push({ period: key, label: formatPeriodLabel(key, periodType), rows: map.get(key)! });
  }
  return groups;
}

interface ColumnGroup {
  label: string;
  span: number;
}

function DataTable({ rows, columns, summaryRow, userLevel, periodType, columnGroups, loading }: {
  rows: ChannelRow[];
  columns: TableDef[];
  summaryRow?: ChannelRow | null;
  userLevel: number;
  periodType: PeriodType;
  columnGroups?: ColumnGroup[];
  loading?: boolean;
}) {
  const cellVal = (c: TableDef, row: ChannelRow) => {
    if (c.key === 'channel') return row.channel;
    if ((c.key === 'reach' || c.key === 'frequency') && !supportsReach(row)) return '-';
    const raw = c.fmt(row[c.key] as number);
    return c.permKey ? (sf(raw, c.permKey, userLevel).value ?? '🔒') : raw;
  };

  const hasPeriod = rows.length > 0 && rows.some(r => r.period);
  const groups = hasPeriod ? groupByPeriod(rows, periodType) : [];
  const totalCols = columns.length + (hasPeriod ? 1 : 0);
  const sortValue = useCallback((row: ChannelRow, key: string): SortValue => {
    if ((key === 'reach' || key === 'frequency') && !supportsReach(row)) return null;
    return row[key as keyof ChannelRow] as SortValue;
  }, []);
  const { sort, toggleSort, sortedRows, setSort } = useTableSort<ChannelRow, string>(rows, sortValue);

  useEffect(() => {
    if (!sort) return;
    const column = columns.find(item => item.key === sort.key);
    if (column?.permKey && sf('0', column.permKey, userLevel).display !== 'show') setSort(null);
  }, [columns, setSort, sort, userLevel]);

  const sortedGroups = useMemo(
    () => groups.map(group => ({ ...group, rows: sortRows(group.rows, sort, sortValue) })),
    [groups, sort, sortValue],
  );

  const groupBoundaries = useMemo(() => {
    if (!columnGroups || columnGroups.length <= 1) return new Set<number>();
    const set = new Set<number>();
    let offset = 0;
    for (let i = 0; i < columnGroups.length; i++) {
      if (i > 0) set.add(offset);
      offset += columnGroups[i].span;
    }
    return set;
  }, [columnGroups]);

  const cellBorder = (colIdx: number): React.CSSProperties =>
    groupBoundaries.has(colIdx) ? { borderLeft: '2.5px solid var(--border2)' } : {};

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ minWidth: 800 }}>
        <thead>
          {columnGroups && columnGroups.length > 0 && (
            <tr>
              {hasPeriod && <th rowSpan={2} style={{ verticalAlign: 'middle' }}>日期</th>}
              {columnGroups.map((g, gi) => (
                <th key={g.label} colSpan={g.span} style={{
                  textAlign: 'center',
                  fontWeight: 700,
                  fontSize: 12,
                  background: gi === 0 ? 'rgba(0,0,0,.03)' : 'rgba(59,130,246,.08)',
                  color: gi === 0 ? 'var(--text2)' : '#1d4ed8',
                  borderBottom: '2px solid var(--border2)',
                  borderLeft: gi > 0 ? '2.5px solid var(--border2)' : undefined,
                }}>{g.label}</th>
              ))}
            </tr>
          )}
          <tr>
            {(!columnGroups || columnGroups.length === 0) && hasPeriod && <th>日期</th>}
            {columns.map((c, ci) => (
              <SortableHeader
                key={c.key}
                sortKey={String(c.key)}
                sort={sort}
                onSort={toggleSort}
                align={c.key !== 'channel' ? 'right' : 'left'}
                disabled={Boolean(c.permKey && sf('0', c.permKey, userLevel).display !== 'show')}
                style={{
                  ...(c.key !== 'channel' ? { textAlign: 'right' as const } : {}),
                  ...cellBorder(ci),
                }}
              >
                {c.label}
              </SortableHeader>
            ))}
          </tr>
        </thead>
        <tbody>
          {summaryRow && (
            <tr style={{ fontWeight: 600, background: '#fffbeb', fontSize: 12 }}>
              {hasPeriod && <td style={{ fontWeight: 600 }}>合计</td>}
              {columns.map((c, ci) => (
                <td key={c.key} style={{
                  ...(c.key !== 'channel' ? { textAlign: 'right' as const, fontVariantNumeric: 'tabular-nums' as const } : { fontWeight: 600 }),
                  ...cellBorder(ci),
                }}>
                  {cellVal(c, summaryRow)}
                </td>
              ))}
            </tr>
          )}
          {rows.length === 0 && loading ? (
            <tr><td colSpan={totalCols}><TableLoadingCell /></td></tr>
          ) : rows.length === 0 ? (
            <tr><td colSpan={totalCols} style={{ textAlign: 'center', padding: 32, color: 'var(--text3)' }}>暂无数据</td></tr>
          ) : hasPeriod ? (
            sortedGroups.map(group =>
              group.rows.map((row, idx) => (
                <tr key={group.period + row.channel + idx}>
                  {idx === 0 && (
                    <td
                      rowSpan={group.rows.length}
                      style={{ verticalAlign: 'middle', fontWeight: 500, whiteSpace: 'nowrap', background: 'var(--surface)' }}
                    >
                      {group.label}
                    </td>
                  )}
                  {columns.map((c, ci) => (
                    <td key={c.key} style={{
                      ...(c.key !== 'channel' ? { textAlign: 'right' as const, fontVariantNumeric: 'tabular-nums' as const } : {}),
                      ...cellBorder(ci),
                    }}>
                      {cellVal(c, row)}
                    </td>
                  ))}
                </tr>
              ))
            )
          ) : (
            sortedRows.map((row, idx) => (
              <tr key={row.channel + idx}>
                {columns.map((c, ci) => (
                  <td key={c.key} style={{
                    ...(c.key !== 'channel' ? { textAlign: 'right' as const, fontVariantNumeric: 'tabular-nums' as const } : {}),
                    ...cellBorder(ci),
                  }}>
                    {cellVal(c, row)}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────

const PERIOD_TABS: { key: PeriodType; label: string }[] = [
  { key: 'day', label: '日' },
  { key: 'week', label: '周' },
  { key: 'month', label: '月' },
  { key: 'quarter', label: '季' },
  { key: 'year', label: '年' },
];

export default function ChannelMonitoring() {
  const { brandId, siteId, adContext } = useAppStore();
  const userLevel = adContext?.data_level ?? 1;
  const [periodType, setPeriodType] = useState<PeriodType>('day');
  const [dayStart, setDayStart] = useState(() => daysAgo(7));
  const [dayEnd, setDayEnd] = useState(() => today());
  const [periodStarts, setPeriodStarts] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<AllViewsResponse | null>(null);

  const weekOptions = useMemo(() => generateWeekOptions(), []);
  const monthOptions = useMemo(() => generateMonthOptions(), []);
  const quarterOptions = useMemo(() => generateQuarterOptions(), []);
  const yearOptions = useMemo(() => generateYearOptions(), []);

  const isMultiPeriod = periodType !== 'day';

  useEffect(() => {
    if (periodType !== 'day' && periodStarts.length === 0) {
      const src = periodType === 'week' ? weekOptions
        : periodType === 'month' ? monthOptions
        : periodType === 'quarter' ? quarterOptions
        : yearOptions;
      if (src.length > 0) setPeriodStarts([src[0].value]);
    }
  }, [periodType, weekOptions, monthOptions, quarterOptions, yearOptions, periodStarts.length]);

  const handlePeriodChange = useCallback((p: PeriodType) => {
    setPeriodType(p);
    setPeriodStarts([]);
  }, []);

  const fetchData = useCallback(async () => {
    if (periodType === 'day') {
      if (!dayStart || !dayEnd) return;
      setLoading(true);
      try {
        const res = await channelMonitoringApi.getData({ brand_id: brandId, site_id: siteId || undefined, period_type: 'day', start_date: dayStart, end_date: dayEnd });
        setData(res.data);
      } catch (e) {
        console.error('Failed to load channel monitoring data:', e);
        setData(null);
      } finally {
        setLoading(false);
      }
    } else {
      if (periodStarts.length === 0) return;
      setLoading(true);
      try {
        const res = await channelMonitoringApi.getData({ brand_id: brandId, site_id: siteId || undefined, period_type: periodType, period_start: periodStarts.join(',') });
        setData(res.data);
      } catch (e) {
        console.error('Failed to load channel monitoring data:', e);
        setData(null);
      } finally {
        setLoading(false);
      }
    }
  }, [brandId, siteId, periodType, dayStart, dayEnd, periodStarts]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const summaryRows   = data?.channel_summary ?? [];
  const upperRows     = data?.channel_upper ?? [];
  const middleRows    = data?.channel_middle ?? [];
  const lowerRows     = data?.channel_lower ?? [];
  const layerRows     = data?.layer_total ?? [];
  const detailRows    = data?.layer_detail ?? [];

  const summaryTotal  = summaryRows.length > 0 ? sumChannelRows(summaryRows, '合计') : null;
  const upperTotal    = upperRows.length > 0 ? sumChannelRows(upperRows, '合计') : null;
  const middleTotal   = middleRows.length > 0 ? sumChannelRows(middleRows, '合计') : null;
  const lowerTotal    = lowerRows.length > 0 ? sumChannelRows(lowerRows, '合计') : null;
  const layerTotal    = layerRows.length > 0 ? sumChannelRows(layerRows, '合计') : null;
  const detailTotal   = detailRows.length > 0 ? sumChannelRows(detailRows, '合计') : null;

  const showNewCustomer = periodType !== 'week';
  const summaryCols = showNewCustomer ? COLS_SUMMARY : [...BASE_COLS, ...RATIO_COLS];
  const summaryGroups: ColumnGroup[] | undefined = showNewCustomer
    ? [{ label: '站外数据', span: BASE_COLS.length + RATIO_COLS.length }, { label: '新客数据', span: NEW_CUSTOMER_COLS.length }]
    : undefined;

  const tables: { title: string; rows: ChannelRow[]; columns: TableDef[]; summary: ChannelRow | null; groups?: ColumnGroup[] }[] = [
    { title: '渠道汇总', rows: summaryRows, columns: summaryCols, summary: summaryTotal, groups: summaryGroups },
    { title: '渠道-上层合计', rows: upperRows, columns: COLS_UPPER, summary: upperTotal },
    { title: '渠道中层', rows: middleRows, columns: COLS_MIDDLE, summary: middleTotal },
    { title: '渠道下层', rows: lowerRows, columns: COLS_LOWER, summary: lowerTotal },
    { title: '分层合计', rows: layerRows, columns: COLS_LAYER_TOT, summary: layerTotal },
    { title: '分层明细', rows: detailRows, columns: COLS_DETAIL, summary: detailTotal },
  ];
  const hasAnyRows = tables.some(table => table.rows.length > 0);
  const visibleTables = loading && !hasAnyRows ? tables.slice(0, 1) : tables;

  const currentSelectOptions: SelectOption[] = useMemo(() => {
    const src = periodType === 'week' ? weekOptions
      : periodType === 'month' ? monthOptions
      : periodType === 'quarter' ? quarterOptions
      : periodType === 'year' ? yearOptions
      : [];
    return src.map(o => ({ value: o.value, label: o.label }));
  }, [periodType, weekOptions, monthOptions, quarterOptions, yearOptions]);

  return (
    <div className="page-section visible">
      {/* Period Filter */}
      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end',
        padding: '14px 16px',
        background: 'var(--surface)', borderRadius: 'var(--radius-sm)',
        border: '1px solid var(--border2)', marginBottom: 12,
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label style={{ fontSize: 11, fontWeight: 500, color: 'var(--text3)' }}>时间维度</label>
          <div className="page-tabs" style={{ marginBottom: 0 }}>
            {PERIOD_TABS.map(p => (
              <div key={p.key} className={`page-tab${periodType === p.key ? ' active' : ''}`} onClick={() => handlePeriodChange(p.key)}>
                {p.label}
              </div>
            ))}
          </div>
        </div>

        {periodType === 'day' && (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label style={{ fontSize: 11, fontWeight: 500, color: 'var(--text3)' }}>开始日期</label>
              <input type="date" className="form-input" value={dayStart} onChange={e => setDayStart(e.target.value)} style={{ width: 160, height: 34 }} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label style={{ fontSize: 11, fontWeight: 500, color: 'var(--text3)' }}>结束日期</label>
              <input type="date" className="form-input" value={dayEnd} onChange={e => setDayEnd(e.target.value)} style={{ width: 160, height: 34 }} />
            </div>
          </>
        )}

        {periodType !== 'day' && currentSelectOptions.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 220 }}>
            <label style={{ fontSize: 11, fontWeight: 500, color: 'var(--text3)' }}>
              {periodType === 'week' ? '选择周' : periodType === 'month' ? '选择月' : periodType === 'quarter' ? '选择季度' : '选择年'}
            </label>
            <MultiSelect
              single={!isMultiPeriod}
              searchable
              options={currentSelectOptions}
              value={periodStarts}
              onChange={v => setPeriodStarts(isMultiPeriod ? v : v.length > 0 ? [v[0]] : [])}
              placeholder="请选择"
            />
          </div>
        )}
      </div>

      {/* 6 Tables */}
      {visibleTables.map(({ title, rows, columns, summary, groups }, index) => (
        <div className="card" style={{ marginTop: 16 }} key={title}>
          <div className="card-header">
            <div className="card-title">{title}</div>
          </div>
          <div style={{ position: 'relative' }}>
            <DataTable
              rows={rows}
              columns={columns}
              summaryRow={summary}
              userLevel={userLevel}
              periodType={periodType}
              columnGroups={groups}
              loading={loading && !hasAnyRows && index === 0}
            />
            {loading && rows.length > 0 && <TableLoadingOverlay />}
          </div>
        </div>
      ))}
    </div>
  );
}
